<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>
    <div class="sl-app-layout">
        <!-- Global Overlay -->
        <div v-if="showOverlay" class="fixed inset-0 z-70 flex justify-center items-center bg-gray-500/75 backdrop-blur-sm select-none">
            <div class="text-center text-xl tracking-wide text-gray-800">
                {{ overlayText }}
            </div>
        </div>

        <!-- Navbar -->
        <div class="sl-navbar">
            <div class="container mx-auto h-full flex">
                <!-- Main Menu -->
                <main-menu-burger />

                <!-- Home (SwissLens Logo) -->
                <router-link :to="{ name: 'home', params: $route.params }" class="sl-logo__wrapper">
                    <img
                        v-if="isChristmasTime"
                        class="sl-logo sl-logo--christmas"
                        src="/img/logo-swisslens-white-christmas.svg"
                    >

                    <img
                        v-else
                        class="sl-logo"
                        src="/img/logo-swisslens-white.svg"
                    >
                </router-link>

                <!-- Main Menu -->
                <main-menu />

                <div class="sl-navbar__icon-list">
                    <!-- Orders Waiting for Review -->
                    <template v-if="$store.getters['account/isStaff']">
                        <div
                            v-if="fetchingWaitingForReview"
                            class="sl-navbar__icon-item cursor-default"
                        >
                            <mdi-icon icon="mdiLoading" class="animate-spin" />
                        </div>

                        <div
                            v-else
                            class="sl-navbar__icon-item"
                            @click="setStaffTool('review-requests')"
                        >
                            <template v-if="reviewRequests.length">
                                <mdi-icon
                                    icon="mdiAlertBox"
                                    :class="{ 'sl-navbar__icon-alert--assigned': myOrNoOnesReviewRequests.length }"
                                />

                                <mdi-icon
                                    v-if="urgentUnreadComments.length"
                                    icon="mdiMessageText"
                                    class="sl-navbar__icon-message text-red-500"
                                />

                                <mdi-icon
                                    v-else-if="myUnreadComments.length"
                                    icon="mdiMessageText"
                                    class="sl-navbar__icon-message text-yellow-400"
                                />

                                <div
                                    v-if="urgentUnassignedReviewRequests.length"
                                    class="sl-navbar__icon-warning sl-navbar__icon-warning--pulse"
                                >
                                </div>
                            </template>

                            <mdi-icon v-else icon="mdiCheckboxMarked" />
                        </div>

                        <div class="sl-navbar__separator"></div>
                    </template>

                    <!-- Internal Note on Entity -->
                    <template v-if="$store.getters['account/isStaff']">
                        <div class="sl-navbar__icon-item" @click="$store.dispatch('account/toggleEntityInternalNotePanel')">
                            <mdi-icon icon="mdiCardAccountDetailsOutline" />

                            <div v-if="!entityInternalNoteIsEmpty" class="sl-navbar__icon-dot bg-yellow-400"></div>
                        </div>

                        <div class="sl-navbar__separator"></div>
                    </template>

                    <!-- Basket -->
                    <basket-menu />

                    <div class="sl-navbar__separator"></div>

                    <!-- Prices Visibility Toggle -->
                    <prices-visibility-menu v-can="'price:view'" />

                    <div v-can="'price:view'" class="sl-navbar__separator"></div>

                    <!-- Languages Menu -->
                    <languages-menu />

                    <div class="sl-navbar__separator"></div>

                    <!-- Account & Drop-Down Menu -->
                    <dropdown-menu />

                    <!-- Entities Switch Button (only if current user has access to more than one) -->
                    <template v-if="entitiesCount > 1">
                        <div class="sl-navbar__separator"></div>
                        <div class="sl-navbar__icon-item sl-dropdown-menu" @click="goToEntitiesSwitch">
                            <mdi-icon icon="mdiAccountSwitch" />
                        </div>
                    </template>
                </div>
            </div>

            <!-- Arrow/Dent pointing to the current main menu item -->
            <div class="sl-navbar__dent" :style="dentStyle"></div>
        </div>

        <!-- App Content -->
        <div class="sl-container container">
            <!-- Secondary Menu -->
            <secondary-menu />

            <!-- Notification Band -->
            <div class="sl-notifications-band">
                <transition-group name="fade">
                    <div
                        v-for="(notification, index) in $store.state.notifications.band"
                        :key="notification"
                        class="sl-notification"
                        :class="{
                            'sl-notification--dismissible': notification.dismissible,
                            'sl-notification--info': notification.theme === 'info',
                            'sl-notification--warn': notification.theme === 'warn',
                            'sl-notification--danger': notification.theme === 'danger',
                            'sl-notification--success': notification.theme === 'success',
                            'sl-notification--disabled': notification.theme === 'disabled',
                        }"
                    >
                        <mdi-icon
                            v-if="notification.dismissible"
                            :key="index"
                            icon="mdiClose"
                            class="sl-notification__dismiss"
                            @click="dismissNotification(notification.id)"
                        />

                        <div class="sl-notification__title">
                            {{ notification.title }}
                        </div>

                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div class="sl-notification__text" v-html="formatNotificationText(notification.text)"></div>
                    </div>
                </transition-group>
            </div>

            <!-- Current view will come here -->
            <router-view v-slot="{ Component }">
                <transition name="fast-fade" mode="out-in">
                    <component :is="Component" />
                </transition>
            </router-view>

            <!-- Footer -->
            <sl-footer />
        </div>

        <entity-internal-note-panel
            :is-open="$store.state.account.entityInternalNotePanelIsOpen"
            @close="$store.dispatch('account/toggleEntityInternalNotePanel', false)"
        />

        <!-- Staff Tool Overlay -->
        <teleport to="body">
            <transition name="big-bang">
                <div
                    v-if="staffTool"
                    class="fixed top-0 left-0 z-50 w-screen h-screen p-8 bg-white/90 text-gray-500 backdrop-blur"
                >
                    <!-- Close Button -->
                    <mdi-icon
                        v-tippy="{ placement: 'left' }"
                        :content="$t('common.close')"
                        icon="mdiClose"
                        class="cursor-pointer absolute top-8 right-8 w-8 h-8 text-gray-500 transition hover:text-primary-500"
                        @click="setStaffTool(null)"
                    />

                    <div class="flex flex-col gap-8 w-full h-full">
                        <div v-if="staffTool.title" class="shrink-0 text-2xl text-primary-500 font-thin">
                            {{ staffTool.title }}
                        </div>

                        <component :is="staffTool.component" />
                    </div>
                </div>
            </transition>
        </teleport>
    </div>
</template>


<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>
import { defineAsyncComponent } from 'vue';
import BasketMenu from './BasketMenu.vue';
import DropdownMenu from './DropdownMenu.vue';
import SlFooter from './Footer.vue';
import LanguagesMenu from './LanguagesMenu.vue';
import MainMenu from './MainMenu.vue';
import MainMenuBurger from './MainMenuBurger.vue';
import PricesVisibilityMenu from './PricesVisibilityMenu.vue';
import NotificationMenu from './NotificationMenu.vue';
import SearchBox from './SearchBox.vue';
import SecondaryMenu from './SecondaryMenu.vue';
import EntityInternalNotePanel from '@/components/order/EntityInternalNotePanel.vue';
import moment from 'moment';
import { has } from 'lodash-es';
import { useToast } from 'vue-toastification';
import ReviewRequest from '@/models/ReviewRequest';
import { useReviewRequestComposable } from '@/composables/ReviewRequestComposable';

export default
{
    name: 'AppLayout',

    components:
    {
        BasketMenu,
        DropdownMenu,
        SlFooter,
        LanguagesMenu,
        MainMenu,
        MainMenuBurger,
        PricesVisibilityMenu,
        NotificationMenu,
        SecondaryMenu,
        SearchBox,
        EntityInternalNotePanel,
    },

    setup()
    {
        const { hasUnreadReviewComments } = useReviewRequestComposable();

        return {
            hasUnreadReviewComments,
        };
    },

    data()
    {
        return {
            /** @type {?{name: string, title: string, component: Object}} */
            staffTool:     null,
            prevStaffTool: '',
        };
    },

    computed:
    {
        initializationStatus()
        {
            return this.$store.state.initializationStatus;
        },

        entitiesCount()
        {
            return this.$store.state.account.entitiesCount;
        },

        showOverlay()
        {
            return this.$store.state.overlay.show;
        },

        overlayText()
        {
            return this.$store.state.overlay.text;
        },

        dentStyle()
        {
            const out = {
                left: '-10px',
            };

            const dimensions = this.$store.state.cMainMenuElement?.$el.getBoundingClientRect();
            if(!dimensions || dimensions.left <= 0 || dimensions.width <= 0)
            {
                return out;
            }

            // Align the dent to the middle of the current menu item
            out.left = dimensions.left + (dimensions.width / 2) - 5;
            out.left += 'px';

            return out;
        },

        entityInternalNoteIsEmpty()
        {
            const div = document.createElement('div');
            div.innerHTML = this.$store.state.account.cEntity.internal_note || '';

            return div.textContent.trim().length === 0;
        },

        fetchingWaitingForReview()
        {
            return this.$store.state.orders.fetchingWaitingForReview;
        },

        /**
         * The list of all active review requests.
         *
         * @returns {ReviewRequest[]}
         */
        reviewRequests()
        {
            return this.$store.state.orders.waitingForReview;
        },

        /**
         * The list of review requests that are assigned to the current user.
         *
         * @returns {ReviewRequest[]}
         */
        myReviewRequests()
        {
            const u = this.$store.state.account.cUser.username;
            return this.reviewRequests.filter(r => r.assigned_to?.username === u);
        },

        /**
         * The list of review requests that are assigned to the current user, or
         * to no one.
         *
         * @returns {ReviewRequest[]}
         */
        myOrNoOnesReviewRequests()
        {
            const u = this.$store.state.account.cUser.username;
            return this.reviewRequests.filter(r =>
            {
                const assignedTo = r.assigned_to?.username;
                if(!assignedTo)
                {
                    return true;
                }

                return assignedTo === u;
            });
        },

        /**
         * The review requests assigned to the current user or to no one.
         *
         * @returns {ReviewRequest[]}
         */
        urgentReviewRequests()
        {
            return this.myOrNoOnesReviewRequests.filter(
                r => r.order.logistics_data?.URGENT
            );
        },

        urgentUnassignedReviewRequests()
        {
            return this.urgentReviewRequests.filter(r => !r.assigned_to);
        },

        myUnreadComments()
        {
            return this.myReviewRequests.filter(
                r => this.hasUnreadReviewComments(r)
            );
        },

        urgentUnreadComments()
        {
            return this.urgentReviewRequests.filter(
                r => this.hasUnreadReviewComments(r)
            );
        },

        staffTools()
        {
            return {
                'review-requests':
                {
                    title:    this.$t('review_requests.page_title'),
                    importer: () => import('@/views/review-requests/ReviewRequestsStaffTool.vue'),
                },
            };
        },

        /**
         * Whether the current month is December.
         *
         * @returns {boolean}
         */
        isChristmasTime()
        {
            return new Date().getMonth() === 11; // months are zero-based in JS
        },
    },

    mounted()
    {
        if(this.$store.getters['account/isStaff'])
        {
            this.$store.dispatch('account/fetchEntityInternalNote');
        }

        // Start fetching review requests
        const eid = this.$store.getters['account/isStaff']
            ? ''                                    // staff: all requests
            : this.$store.state.account.cEntity.id; // customers: only theirs
        this.$store.dispatch('orders/startWaitingForReviewRefreshInterval', {
            eid,
            onError: error =>
            {
                useToast().error(
                    this.$t('review_requests.error_fetching'),
                    { timeout: 3000 }
                );
            },
        });
    },

    beforeUnmount()
    {
        // Stop fetching review requests
        this.$store.dispatch('orders/stopWaitingForReviewRefreshInterval');
    },

    methods:
    {
        goToEntitiesSwitch()
        {
            this.$store.dispatch('onBeforeEntitySwitch');
            this.$router.push({ name: 'entities-switch', params: this.$route.params });
        },

        formatNotificationText(text)
        {
            return text.replace(/(\r\n|\r|\n)/g, '<br>') // new lines
                .replace( // urls
                    /((?:https?:\/{2})(?:[a-z0-9]{2,}\.?){2,}(?:\/[a-z0-9\-]*)*)/gmi,
                    '<a href="$1" target="_blank" class="sl-notification__link">$1</a>'
                );
        },

        dismissNotification(id)
        {
            this.$store.dispatch("notifications/removeBandNotification", id, { root: true });
        },

        /**
         * Open or close the staff tool overlay.
         *
         * @param {?string} name The name of the tool, or null to close the overly.
         * @returns {void}
         */
        setStaffTool(name)
        {
            const tool = this.staffTools[name] ?? null;
            if(tool === null)
            {
                // Close the staff tool overlay
                this.staffTool = null;
                return;
            }

            // Dynamically load selected staff tool
            this.staffTool = {
                name,
                title:     tool.title,
                component: defineAsyncComponent(tool.importer),
            };
        },
    },
};
</script>


<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style>
@reference "#main.css";

/*--- logo ---*/

.sl-logo__wrapper
{
    width: 155px;
    @apply flex items-center justify-start;
}

.sl-logo
{
    height: 20px;
}

.sl-logo--christmas
{
    height: 30px;
    margin-bottom: 8px;
}

/*--- vertical separator ---*/

.sl-navbar__separator
{
    height: 20px;
    @apply align-top inline-block text-center border-l border-primary-200;
}

/*--- icons ---*/

.sl-navbar__icon-list
{
    margin-left: auto;
    width: 255px;
    @apply h-full flex items-center justify-end;
}

.sl-navbar__icon-item
{
    width: 50px;
    @apply relative inline-block cursor-pointer h-full text-center;
}

.sl-navbar__icon-item > .mdi-icon:first-child
{
    @apply m-[13px] w-6 h-6 text-white;
}

.sl-navbar__icon-item > .mdi-icon.sl-navbar__icon-alert--assigned:first-child
{
    @apply relative;
}

.sl-navbar__icon-item > .mdi-icon.sl-navbar__icon-alert--assigned:first-child::after
{
    @apply absolute -z-[1] rounded-full bg-yellow-600;

    content: "";
    inset: 3px;
    animation: sonar-animation 1.5s infinite;
}

.sl-navbar__icon-dot
{
    @apply absolute top-2 right-2 w-3 h-3 rounded-full;
}

.sl-navbar__icon-message
{
    @apply absolute bottom-1 right-2 w-4 h-4;
}

.sl-navbar__icon-warning
{
    width: 0px;
    height: 0px;
    border-style: solid;
    border-width: 0 8px 15px 8px;
    border-color: transparent transparent theme('colors.red.600') transparent;
    transform: rotate(0deg);
    @apply absolute top-2 right-2;
}

.sl-navbar__icon-warning--pulse
{
    animation: pulse-animation 2s infinite;
}

.sl-navbar__icon-warning::after
{
    content: '!';
    margin-left: -1.5px;
    font-size: 0.65rem;
    @apply text-white text-center;
}

@keyframes pulse-animation
{
  0% {
    box-shadow: 0 0 0 0px rgba(0, 0, 0, 0.4);
  }
  100% {
    box-shadow: 0 0 0 40px rgba(0, 0, 0, 0);
  }
}

@keyframes sonar-animation
{
    0% {
        transform: scale(1);
        opacity: 1;
        background-color: theme('colors.red.500');
    }

    25% {
        opacity: 1;
    }

    100% {
        transform: scale(3.5);
        opacity: 0;
        background-color: theme('colors.yellow.400');
    }
}

/*--- Navbar dent ---*/
.sl-navbar__dent
{
    position: fixed;
    /* `left` is set dynamically to the offset of the current main menu item (from $route.meta) */
    top: 30px;
    display: block;
    width: 20px;
    height: 20px;
    border: 10px solid transparent;
    border-bottom-color: theme('colors.gray.100');
}

/*--- Notifications band ---*/
.sl-notifications-band
{
    @apply sticky top-0;

    /* Fade animation (for dismissal) */
    & .fade-enter-active,
    & .fade-leave-active
    {
        transition: opacity 1s ease;
    }

    & .fade-enter-from,
    & .fade-leave-to
    {
        @apply opacity-0;
    }

    /* A notification */
    & > .sl-notification
    {
        @apply relative p-2 text-sm text-center;

        /* The notification's "dismiss" icon/button */
        & > .sl-notification__dismiss
        {
            @apply cursor-pointer absolute top-2 right-2 w-4 h-4;
        }

        /* The notification's title */
        & > .sl-notification__title
        {
            @apply font-bold;
        }

        /* Any link in the notification's text */
        & > .sl-notification__text
        {
            & .sl-notification__link
            {
                @apply text-primary-600 hover:underline;
            }
        }


        /*--- Color themes ---*/

        &.sl-notification--info
        {
            @apply bg-primary-100 text-primary-600;
        }

        &.sl-notification--warn
        {
            @apply bg-yellow-100 text-yellow-700;
        }

        &.sl-notification--danger
        {
            @apply bg-red-100 text-red-700;
        }

        &.sl-notification--success
        {
            @apply bg-green-100 text-green-700;
        }

        &.sl-notification--disabled
        {
            @apply bg-gray-100 text-gray-700;
        }
    }
}
</style>
